import React, { useState , useEffect, useMemo, useRef } from 'react';
import Fuse from 'fuse.js';
import swal from '@sweetalert/with-react';

import Dropdown from '../../Dropdown';
import Portal from '../../Portal';
import BulkEditCourseRequest from '../../BulkEditCourseRequest';
import Toggle from '../../Toggle';

import StudentRequests from '../reports/student_requests';

import { loadSpecificData, calculateConflicts, isAlternateCourseRequest, getSections } from '../../../js/Helpers';
import { updateLabSections } from '../../../js/Sections';
import { buildPeriodsToShow, getPeriodTypeFromScheduleType } from '../../../js/PeriodHelpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';
import { buildCourseSectionLabelMap, buildLabelsById, getVisibleRequestLabels } from '../utils/studentLabelHelpers';
import { getLabelTextColorClass } from '../utils/labelColorUtils';
import {
	addStudentRequestToDraft,
	buildRequestSelectionKey,
	cloneStudentRequestsForDraft,
	extractStudentRequestsForCourse,
	getStudentsRequestingCourseWithDetails,
	replaceCourseIdInStudentRequests,
	updateDraftCourseRequestField,
	removeDraftCourseRequests,
	studentHasActiveCourseRequest,
} from '../utils/courseRequestDraftHelpers';

import '../../../css/hsms/edit_modals.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlusCircle, faTimes, faSearch, faSquare, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faSave, faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';
import { useConflictActions, useConflictData } from '../store/magnetboardStore';
import { filterOutConflicts } from '../../../js/Conflicts';
import { CONFLICT_TYPES } from '../../../js/Constants';

export default function EditCourseData({ dataID, toggleEditData, schoolInfo, scheduleStructureData, scheduleInfo, teachers, students, courses, classrooms, departments, sections, setSections = () => {}, labels, setDatabaseData, setStudents = () => {}, defaultDepartmentID = null, filteredResults = [], setFilteredResults = ()=>{} }) {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState({});
	
	const [tctResults, setTctResults] = useState([]);
	const [classroomRestrictionResults, setClassroomRestrictionResults] = useState([]);
	
	const [addTctOpen, setAddTctOpen] = useState(false);
	const [addClassroomRestrictionOpen, setAddClassroomRestrictionOpen] = useState(false);
	const [addStudentRequestOpen, setAddStudentRequestOpen] = useState(false);
	const [editRequestsOpen, setEditRequestsOpen] = useState(false);
	const [draftStudents, setDraftStudents] = useState([]);
	const [studentRequestResults, setStudentRequestResults] = useState([]);

	const [selectedStudents, setSelectedStudents] = useState([]);
	
	const [changesMade, setChangesMade] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const courseSectionLabelMap = useMemo(() => buildCourseSectionLabelMap(sections), [sections]);
	const labelsByID = useMemo(() => buildLabelsById(labels), [labels]);
	const studentsRef = useRef(students);
	const originalIsSeperateLabRef = useRef(null);

	const { setConflicts, setMatchPercent, markConflictDataFresh } = useConflictActions();
	const { conflicts } = useConflictData();

	useEffect(() => {
		studentsRef.current = students;
	}, [students]);
	
	////////////////////////////////
	///// SET DROPDOWN OPTIONS /////
	////////////////////////////////
	const primarySecondaryOptions = [{value:'1', display:'Primary'}, {value:'2', display:'Back Up'}];
	
	let maxNumSectionsOptions = [];
	for (let i = 0; i < 40; i++) {
		const display = (i === 0) ? 'None' : i;
		maxNumSectionsOptions.push({value:`${i}`, display:display});
	}
	
	let maxClassSizeOptions = [];
	for (let i = 0; i < 1000; i++) {
	  maxClassSizeOptions.push({value:`${i}`, display:i});
	}
	
	const semesterOptions = [
		{value:'FY', display: 'FY - Full Year'},
		{value:'S', display: 'S - Semester'},
		{value:'S1', display: 'S1 - Semester 1'},
		{value:'S2', display: 'S2 - Semester 2'},
		{value:'Q', display: 'Q - Quarter'},
		{value:'Q1', display: 'Q1 - Quarter 1'},
		{value:'Q2', display: 'Q2 - Quarter 2'},
		{value:'Q3', display: 'Q3 - Quarter 3'},
		{value:'Q4', display: 'Q4 - Quarter 4'},
	];

	const electiveOptions = [
		{value:'0', display:'Non-elective'},
		{value:'1', display:'Elective'},
	];
	
	const spanOptions = ['1', '2', '3', '4', '5', '6'];
		
	const toggleAddNewConnection = (connection_type) => {
		if(connection_type === 'tct')
		{
			setAddTctOpen(!addTctOpen);
			setTctResults(addTctOpen ? [] : [...teachers]);
		}
		else if(connection_type === 'classroom_restriction')
		{
			setAddClassroomRestrictionOpen(!addClassroomRestrictionOpen);
			setClassroomRestrictionResults(addClassroomRestrictionOpen ? [] : [...classrooms]);
		}
		else if(connection_type === 'student_request')
		{
			setAddStudentRequestOpen(!addStudentRequestOpen);
			setStudentRequestResults(addStudentRequestOpen ? [] : [...draftStudents]);
		}
	}
	
	const fuzzySearch = (e, dataType) => {
		const search_value = e.target.value;
		
		if(dataType === 'teachers')
		{
			const fuse = new Fuse(teachers, {
				keys: ['name', 'last_name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const teacher_results = search_value.trim().length === 0 ? [...teachers] : results.map(result => result.item);
			
			setTctResults(teacher_results);
		}
		else if(dataType === 'classrooms')
		{
			const fuse = new Fuse(classrooms, {
				keys: ['classroom_name', 'classroom_type'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const classroom_results = search_value.trim().length === 0 ? [...classrooms] : results.map(result => result.item);
			
			setClassroomRestrictionResults(classroom_results);
		}
		else if(dataType === 'students')
		{
			const fuse = new Fuse(draftStudents, {
				keys: ['last_name', 'first_name', 'student_code'],
				threshold: .1
			});

			const results = fuse.search(search_value);
			const student_results = search_value.trim().length === 0 ? [...draftStudents] : results.map(result => result.item).slice(0, 8);
			
			setStudentRequestResults(student_results);
		}
	}

	const toggleSelectStudent = (composite_key) => {
		setSelectedStudents(current_selected_students => {
			const student_selected_index = current_selected_students.findIndex(key => key === composite_key);

			if(student_selected_index !== -1)
			{
				return current_selected_students.filter(key => key !== composite_key);
			}

			return [...current_selected_students, composite_key];
		});
	}
	
	const updateData = (change_type, data_id, data_key, type = null, sub_data_key = null) => {
		if(data_key === 'student_requests')
		{
			if(change_type === 'add')
			{
				setDraftStudents(current_students => addStudentRequestToDraft(current_students, data_id, dataID));
			}
			else if(change_type === 'change')
			{
				const next_value = (sub_data_key === 'is_inclusion')
					? (type === '0' ? '1' : '0')
					: type;
				setDraftStudents(current_students => updateDraftCourseRequestField(current_students, data_id, dataID, sub_data_key, next_value));
			}
			else if(change_type === 'remove')
			{
				setDraftStudents(current_students => removeDraftCourseRequests(current_students, data_id, dataID));
				setSelectedStudents(current_selected_students => current_selected_students.filter(selection_key => selection_key !== data_id));
			}
		}
		else 
		{
			const newData = {...data};
			if(Array.isArray(newData[data_key])) newData[data_key] = [...newData[data_key]];

			if(change_type === 'add')
			{
				const data_to_add = (data_key === 'teachers_can_teach') ? {teacher_id:data_id, type:'1'} : data_id;
				newData[data_key].push(data_to_add);
			}
			else if(change_type === 'change')
			{
				if(data_key === 'teachers_can_teach')
				{
					newData[data_key][data_id] = {...newData[data_key][data_id], type:type};
				}
				else if(data_key === 'departments')
				{

				}
				else
				{
					newData[data_key] = data_id;
				}
			}
			else if(change_type === 'remove')
			{
				newData[data_key].splice(data_id, 1);
			}
		
			setData(newData);
		}
		
		if(change_type !== 'add')
		{
			setAddTctOpen(false);
			setAddClassroomRestrictionOpen(false);
			setAddStudentRequestOpen(false);
			setTctResults([]);
			setClassroomRestrictionResults([]);
		}

		setChangesMade(true);
	}
	
	const handleExitWithoutSaving = async () => {
		let exit_without_saving = true;
		
		if(changesMade)
		{
			const options =  {
				title: "Exit without saving?",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'blue-btn'
					},
				},
				content: (
					<div>
						<p>You have changes that have not been saved. Do you really want to exit without saving?</p>
					</div>
				)
			}
		
			exit_without_saving = await swal(options);
		}
		
		if(exit_without_saving) toggleEditData();
	}

	const updateCourse = () => {
		const trimmed_course_code = data.course_code ? data.course_code.trim() : '';
		const trimmed_name = data.name ? data.name.trim() : '';
		const persisted_course_id = dataID || data.course_id || null;
		const course_code = trimmed_course_code;
		const name = trimmed_name;
		const priority = (data.priority) ? Number(data.priority) : 50;
		const valid_priority = (Number.isInteger(priority) && priority > 0 && priority <= 100);
		
		if(!course_code || !name)
		{
			const options =  {
				title: "Missing Data",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You are missing data! Please input all background info before saving.</p>
					</div>
				)
			}
		
			swal(options);
		}
		else if(!valid_priority)
		{
			const options =  {
				title: "Invalid Priority",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You have an invalid priority number set. It should be an integer value between 1-100!</p>
					</div>
				)
			}
		
			swal(options);
		}
		else
		{
			setIsSaving(true);

			const student_requests = extractStudentRequestsForCourse(draftStudents, persisted_course_id);

			const data_to_save = {
				...data,
				course_id: persisted_course_id,
				course_code: trimmed_course_code,
				name: trimmed_name,
				student_requests: student_requests,
				priority: String(priority),
			};
			
			saveDataToDatabase({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'course', data_to_save:data_to_save}).then(async (response_data) => {
				const saved_course_id = response_data?.new_data_id || persisted_course_id;
				const remapped_student_requests = student_requests.map((request_info) => (
					`${request_info.course_id}` === `${persisted_course_id}`
						? {...request_info, course_id: saved_course_id}
						: request_info
				));
				const saved_course = {
					...data_to_save,
					course_id: saved_course_id,
					student_requests: remapped_student_requests,
				};

				const next_courses = [...courses];
				const next_filtered_results = [...filteredResults];
				const next_students = replaceCourseIdInStudentRequests(draftStudents, persisted_course_id, saved_course_id);
				const next_sections = sections.map((section) => (
					`${section.course_id}` === `${persisted_course_id}`
						? {...section, course_id: saved_course_id, departments: saved_course.departments}
						: section
				));
				
				if(dataID)
				{
					const index_of_specific_data = next_courses.findIndex(course => `${course.course_id}` === `${dataID}`);
					if(index_of_specific_data !== -1) next_courses[index_of_specific_data] = saved_course;

					const filter_results_index = next_filtered_results.findIndex(course => `${course.course_id}` === `${dataID}`);
					if(filter_results_index !== -1) next_filtered_results[filter_results_index] = saved_course;
				}
				else
				{
					next_courses.push(saved_course);
					next_filtered_results.push(saved_course);
				}
				
				setStudents(next_students);
				setSections(next_sections);
				setDatabaseData(next_courses);
				setFilteredResults(next_filtered_results);
				setData(saved_course);

				if (data_to_save.is_separate_lab !== originalIsSeperateLabRef.current) {
					const courseSections = await getSections({ school_id: schoolInfo.school_id, schedule_version_id: schoolInfo.current_schedule_version_id, course_id: saved_course_id, placed: true, labs: true });
					await updateLabSections(courseSections?.sections || [], data_to_save.is_separate_lab, schoolInfo.school_id, schoolInfo.current_schedule_version_id, defaultDepartmentID, setSections);
				}

				setIsSaving(false);
				toggleEditData(null);

				////////////////////////
				/// Update conflicts ///
				////////////////////////
				const conflicts_to_check = [CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY, CONFLICT_TYPES.COURSE_PERIOD_RESTRICTION, CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM, CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED, CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS];

				calculateConflicts({
					school_id: schoolInfo.school_id, 
					schedule_version_id: schoolInfo.current_schedule_version_id, 
					conflicts_to_check: conflicts_to_check,
					match_percent: true
				}).then(conflicts_response => {
					const conflicts_list = conflicts_response.conflicts;
					const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
					setConflicts([...filtered_out_conflicts, ...conflicts_list]);
					setMatchPercent(conflicts_response.match_percent);
					markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
				});
			});
		}
	}
	
	/////////////////////////////
	///// FOCUS ON INPUT ////////
	/////////////////////////////
	useEffect(() => {
		if(addTctOpen) document.getElementById("search-tct-input").focus();
	}, [addTctOpen]);
	
	useEffect(() => {
		if(addClassroomRestrictionOpen) document.getElementById("search-classroom-restriction-input").focus();
	}, [addClassroomRestrictionOpen]);

	useEffect(() => {
		if(addStudentRequestOpen) document.getElementById("search-student-request-input").focus();
	}, [addStudentRequestOpen]);
	
	/////////////////////////
	///// DO ON LOAD ////////
	/////////////////////////
	useEffect(() => {
		if(dataID)
		{
			loadSpecificData({data_type:'course', data_id:dataID, data_version_id:schoolInfo.current_data_version_id, schedule_version_id:schoolInfo.current_schedule_version_id, school_id:schoolInfo.school_id}).then(specific_data => {
				originalIsSeperateLabRef.current = specific_data.is_separate_lab;
				setData(specific_data);
				setDraftStudents(cloneStudentRequestsForDraft(studentsRef.current));
				setSelectedStudents([]);
				setStudentRequestResults([]);
				setIsLoading(false);
			});
		}
		else
		{	
			originalIsSeperateLabRef.current = '0';
			setData({new: true,  course_code: null, name: null, departments:[], classroom_type_id: null, credits:"0", elective:"0", priority:"50", semester:'FY', max_num_sections:"0", max_class_size:"30", num_periods_spanned:"1", is_lunch:"0", allow_icr_students:"0", is_outside_course:"0", is_separate_lab:"0", is_swappable_for_lab:"0", classroom_restrictions: [], period_restrictions:[], student_requests:[], teachers_can_teach:[]});
			setDraftStudents(cloneStudentRequestsForDraft(studentsRef.current));
			setSelectedStudents([]);
			setStudentRequestResults([]);
			setIsLoading(false);
		}
	}, [dataID, schoolInfo.school_id, schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id]);
	
	//console.log(data);
	//console.log(departments);
	//console.log({sections});

	///////////////////////////////////
	/// Display Course Availability ///
	///////////////////////////////////
	const scheduleType = scheduleInfo.schedule_type;
	const period_type = getPeriodTypeFromScheduleType(scheduleType);
	const periodsToShow = buildPeriodsToShow(scheduleStructureData, {
		period_type,
		current_day: '1',
		natural_sort_course_periods: true,
	});
	const availability_course_period_ids = [
		...new Set(periodsToShow.map(period_info => {
			if(scheduleType !== 'daily') return period_info.period_id;

			const schedule_period_info = scheduleStructureData.schedule_structure[1].find(day_row => day_row.schedule_period_id === period_info.period_id);
			return schedule_period_info ? schedule_period_info.course_period_id : null;
		}).filter(course_period_id => !!course_period_id))
	];
	const toggleAllCoursePeriods = () => {
		const next_period_restrictions = data.period_restrictions.length > 0 ? [] : availability_course_period_ids;
		updateData('change', next_period_restrictions, 'period_restrictions');
	}
	const availability_day_nums = Object.keys(scheduleStructureData.schedule_structure).sort((day_a, day_b) => `${day_a}`.localeCompare(`${day_b}`, undefined, { numeric: true, sensitivity: 'base' }));
	const first_availability_day_num = availability_day_nums[0];
	const availability_schedule_period_ids = [
		...new Set((scheduleStructureData.schedule_structure[first_availability_day_num] || []).map((day_row) => `${day_row.schedule_period_id}`)),
		...Object.keys(scheduleStructureData.schedule_periods)
			.filter((schedule_period_id) => !(scheduleStructureData.schedule_structure[first_availability_day_num] || []).some((day_row) => `${day_row.schedule_period_id}` === `${schedule_period_id}`))
			.sort((schedule_period_id_a, schedule_period_id_b) => {
				const period_name_a = scheduleStructureData.schedule_periods[schedule_period_id_a]?.period_name || '';
				const period_name_b = scheduleStructureData.schedule_periods[schedule_period_id_b]?.period_name || '';
				return period_name_a.localeCompare(period_name_b, undefined, { numeric: true, sensitivity: 'base' });
			}),
	];
	const availability_schedule_row_style = {gridTemplateColumns : `150px repeat(${Object.keys(scheduleStructureData.schedule_structure).length}, 1fr)`};

	////////////////////////
	/// Display Requests ///
	////////////////////////
	const sorted_students_requesting_course = getStudentsRequestingCourseWithDetails(draftStudents, dataID);

	const requests_style = (scheduleInfo.separate_ICR_codes === '0') ? {gridTemplateColumns:'20px 1fr 2fr 30px 2fr 1fr 1fr 30px'} : {gridTemplateColumns:'20px 1fr 2fr 30px 2fr 1fr 30px'};
	
	return (
		<div className='modal-screen'>
			<div className='modal-screen-content'>
				{!isSaving && 
					<>
					<span className="data-modal-close" onClick={handleExitWithoutSaving}>&times;</span>
					<div className="modal-save-btn" onClick={updateCourse}><FontAwesomeIcon icon={faSave}/>Save Course</div>
					</>
				}
				<div className='full-width'>
					<div className='edit-data-content'>
						<FontAwesomeIcon className='edit-data-main-head-icon' icon={faEdit}/>
						<h1 className='edit-data-main-header'>{dataID ? 'Edit' : 'Add New'} Course</h1>
						{isSaving &&
							<div className='dashboard-database-message-container'>
								<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
								<div>Saving...</div>
							</div>
						}
						{isLoading &&
							<div className='dashboard-database-message-container'>
								<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
							</div>
						}
						{(!isLoading && !isSaving) &&
							<form className='edit-data-form'>
								<div className='edit-data-grid-25-75'>
									<div>
										<p className='modal-label'>Course Code:</p>
										<input type="text" className='edit-data-input' value={data.course_code || ''} onChange={(e) => updateData('change', e.target.value, 'course_code')}/>
									</div>
									<div>
										<p className='modal-label'>Course Name:</p>
										<input type="text" className='edit-data-input' value={data.name || ''} onChange={(e) => updateData('change', e.target.value, 'name')}/>
									</div>
								</div>
								<div className='edit-data-grid-50'>
									<div className='edit-data-dropdown-container'>
										<div className='data-modal-heading'>Elective</div>
										<Dropdown data={electiveOptions} currentValue={data.elective} handleChange={(new_value) => updateData('change', new_value, 'elective')} />
									</div>
									<div className='edit-data-dropdown-container'>
										<div className='data-modal-heading'>Semester</div>
										<Dropdown data={semesterOptions} currentValue={data.semester} handleChange={(new_value) => updateData('change', new_value, 'semester')} />
									</div>
								</div>
								<div>
									<div className='data-modal-heading'>How many periods does a section of this course span?</div>
									{spanOptions.map((span_num, index) => {
										return (
											<div className={`option-btn ${span_num === data.num_periods_spanned ? 'option-btn-selected' : ''}`} key={index} onClick={(e) => updateData('change', span_num, 'num_periods_spanned')}>{span_num}</div>
										)	
									})}
								</div>
								<div className='edit-data-grid-50'>
									<div className='edit-data-dropdown-container'>
										<div className='data-modal-heading'>Credits</div>
										<input type="text" className='edit-data-input' value={data.credits || ''} onChange={(e) => updateData('change', e.target.value, 'credits')} style={{marginTop:'10px'}}/>
									</div>
									<div className='edit-data-dropdown-container'>
										<div className='data-modal-heading'>Priority</div>
										<div className='small-text' style={{marginTop:'5px'}}>Pick any number 1-100 (1 = highest priority, 100 = lowest priority)</div>
										<input type="text" className='edit-data-input' value={data.priority || ''} onChange={(e) => updateData('change', e.target.value, 'priority')}/>
									</div>
								</div>
								<div>
									<div className='data-modal-heading'>Department</div>
									{departments.map((department_info, index) => {
										const department_id = department_info.department_id;
										const data_department_index = data.departments.indexOf(department_id);
										const data_department_bool = (data_department_index !== -1) ? true : false;
										
										if(data_department_bool)
										{
											return (
												<div className='option-btn option-btn-selected' key={index} onClick={(e) => updateData('remove', data_department_index, 'departments')}>{department_info.department}</div>
											)
										}
										else
										{
											return (
												<div className='option-btn' key={index} onClick={(e) => updateData('add', department_id, 'departments')}>{department_info.department}</div>
											)
										}
									})}
								</div>
								<div>
									<div className='data-modal-heading'>Teachers Who Teach This Course</div>
									<div>
										{data.teachers_can_teach.length > 0 &&
											<div className="edit-data-subheading edit-data-teacher-can-teach-row">
												<div><strong>Course Name</strong></div>
												<div>Primary/Back Up</div>
											</div>
										}
										{data.teachers_can_teach.length === 0 &&
											<div className='edit-data-no-data-container'>No teachers set yet!</div>
										}
										{data.teachers_can_teach.map((tct_info, index) => {
											const teacher_id = tct_info.teacher_id;
											const primary_secondary = tct_info.type;
											
											const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
											if(!teacher_info) return null;
											
											return (
												<div className='table-row-inv edit-data-teacher-can-teach-row' key={index}>
													<div>{teacher_info.name}, {teacher_info.first_name}</div>
													<div>
														<Dropdown data={primarySecondaryOptions} currentValue={primary_secondary} handleChange={(new_value) => updateData('change', index, 'teachers_can_teach', new_value)} />
													</div>
													<div className="remove-data" onClick={() => updateData('remove', index, 'teachers_can_teach')}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											)	
										})}
									</div>
									<div className='edit-data-add-new-btn blue-link' onClick={() => toggleAddNewConnection('tct')}>
										<FontAwesomeIcon icon={faPlusCircle}/>
										<div>Add Teacher Who Teaches</div>
									</div>
									{addTctOpen &&
										<div className='search-add-row-container'>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-tct-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'teachers')} placeholder='Search for teacher'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('tct')}/>
											</div>
											{tctResults.length !== 0 &&
												<div className='search-add-search-results'>
													<div className='search-add-did-you-mean'>Did you mean:</div>
													{tctResults.map((teacher, i) => {
														const teacher_selected = data.teachers_can_teach.some(tct_info => tct_info.teacher_id === teacher.teacher_id);
														return (
															<div className='search-add-search-result' key={i}>
																<div>{teacher.name}, {teacher.first_name}</div>
																<div>
																	{teacher_selected
																		? <div className='search-add-already-selected'>Selected</div>
																		: <div className='search-add-add-btn' onClick={() => updateData('add', teacher.teacher_id, 'teachers_can_teach')}>Add</div>
																	}
																</div>
															</div>
														)
													})}
												</div>
											}
										</div>
									}
								</div>
								<div>
									<div className='data-modal-heading'>Classroom(s) this Course <span className='yellow-text'>NEEDS</span> to be in</div>
									<div>
										{data.classroom_restrictions.length > 0 &&
											<div className="edit-data-subheading edit-data-teacher-can-teach-row">
												<div><strong>Classroom</strong></div>
												<div>Classroom Type</div>
											</div>
										}
										{data.classroom_restrictions.length === 0 &&
											<div className='edit-data-no-data-container'>No classrooms set yet!</div>
										}
										{data.classroom_restrictions.map((classroom_id, index) => {
											const classroom_info = classrooms.find(classroom => classroom.classroom_id === classroom_id);
											
											return (
												<div className='table-row-inv edit-data-course-classroom-row' key={index}>
													<div>{classroom_info.classroom_name}</div>
													<div>{classroom_info.classroom_type}</div>
													<div className="remove-data" onClick={() => updateData('remove', index, 'classroom_restrictions')}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											)	
										})}
									</div>
									<div className='edit-data-add-new-btn blue-link' onClick={() => toggleAddNewConnection('classroom_restriction')}>
										<FontAwesomeIcon icon={faPlusCircle}/>
										<div>Add Classroom Restriction</div>
									</div>
									{addClassroomRestrictionOpen &&
										<div className='search-add-row-container'>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-classroom-restriction-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'classrooms')} placeholder='Search for classroom or classroom type'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('classroom_restriction')}/>
											</div>
											{classroomRestrictionResults.length !== 0 &&
												<div className='search-add-search-results'>
													<div className='search-add-did-you-mean'>Did you mean:</div>
													{classroomRestrictionResults.map((classroom, i) => {
														const classroom_selected = data.classroom_restrictions.includes(classroom.classroom_id);
														return (
															<div className='search-add-search-result' key={i}>
																<div>{classroom.classroom_name} (<span className='orange-text'>Classroom Type</span>: {classroom.classroom_type})</div>
																<div>
																	{classroom_selected
																		? <div className='search-add-already-selected'>Selected</div>
																		: <div className='search-add-add-btn' onClick={() => updateData('add', classroom.classroom_id, 'classroom_restrictions')}>Add</div>
																	}
																</div>
															</div>
														)
													})}
												</div>
											}
										</div>
									}
								</div>
								<div>
									<div className='data-modal-heading'>Course Availability</div>
									<div className='edit-data-availability-screen'>
										<div className='edit-data-availability-select-sidebar'>
											<h3 className='edit-data-availability-sidebar-header'>Period Availability</h3>
											<div
												className={`small-text edit-data-availability-select-all ${data.period_restrictions.length > 0 ? 'approve-link' : 'cancel-link'}`}
												onClick={toggleAllCoursePeriods}
											>
												{data.period_restrictions.length > 0 ? 'Select All' : 'Unselect All'}
											</div>
											{periodsToShow.map((period_info, index) => {
												const period_id = period_info.period_id;
												const period_name = period_info.period_name;
												const course_period_id = (scheduleType === 'daily') ? scheduleStructureData.schedule_structure[1].find(day_row => day_row.schedule_period_id === period_id).course_period_id : period_id;
												const course_period_index = data.period_restrictions.indexOf(course_period_id);
												
												const course_period_available = (course_period_index !== -1) ? false : true;
												
												return (
													<div className='edit-data-availability-period-option-container' key={period_id}>
														<div>{period_name}</div>
														{course_period_available &&
														<div onClick={() => updateData('add', course_period_id, 'period_restrictions')}>
															<FontAwesomeIcon className='fas-checkbox-checked' icon={faCheckSquare}/>
														</div>
														}
														{!course_period_available &&
														<div onClick={() => updateData('remove', course_period_index, 'period_restrictions')}>
															<FontAwesomeIcon className='fas-checkbox-unchecked dark-gray-text' icon={faSquare}/>
														</div>
														}
													</div>
												)
											})}
										</div>
										<div className='edit-data-availability-display-screen'>
											<div className='edit-data-availability-schedule'>
												<div className='edit-data-availability-schedule-row' style={availability_schedule_row_style}>
													<div>Periods</div>
													{availability_day_nums.map((day_num, index) => {
														return (
															<div className='edit-data-availability-day-heading' key={index}>Day {day_num}</div>
														)
													})}
												</div>
												{availability_schedule_period_ids.map((schedule_period_id) => {
													const schedule_period_info = scheduleStructureData.schedule_periods[schedule_period_id];
													
													return (
														<div className='edit-data-availability-schedule-row' key={schedule_period_id} style={availability_schedule_row_style}>
															<div className='edit-data-availability-period-heading'>{schedule_period_info.period_name}</div>
															{availability_day_nums.map((day_num) => {
																const day_structure = scheduleStructureData.schedule_structure[day_num];
																const structure_row = day_structure.find(day_row => day_row.schedule_period_id === schedule_period_id);
																const course_period_id = (structure_row) ? structure_row.course_period_id : null;
																const course_period_name = (course_period_id) ? scheduleStructureData.course_periods[course_period_id].period_name : null;
																
																const unavailable = data.period_restrictions.includes(course_period_id) ? true : false;
																
																return (
																	<div className={`edit-data-availability-course-period ${unavailable ? 'edit-data-availability-course-period-unavailable' : ''}`} key={`${day_num}_${schedule_period_id}`}>
																		<div className='edit-data-availability-course-period-name'>{course_period_name}</div>
																		<div className='edit-data-availability-status-container'>
																			<div>{unavailable ? 'Unavailable' : 'Available'}</div>
																			<div className={`edit-data-availability-status ${unavailable ? 'red' : 'green'}`}></div>
																		</div>
																	</div>
																)
															})}
														</div>
													)
												})}
											</div>
										</div>
									</div>
								</div>
								<div className='edit-data-grid-50'>
									<div className='edit-data-dropdown-container'>
										<div className='data-modal-heading'>Max Number of Sections</div>
										<Dropdown data={maxNumSectionsOptions} currentValue={data.max_num_sections} handleChange={(new_value) => updateData('change', new_value, 'max_num_sections')} />
									</div>
									<div className='edit-data-dropdown-container'>
										<div className='data-modal-heading'>Max Class Size</div>
										<Dropdown data={maxClassSizeOptions} currentValue={data.max_class_size} handleChange={(new_value) => updateData('change', new_value, 'max_class_size')} />
									</div>
								</div>
								<div>
									<div className='data-modal-heading'>Students Who Have Requested This Course</div>
									<div>
										<div className='edit-course-request-actions-row'>
											<div style={{flex:'1 1 auto'}}>
												{sorted_students_requesting_course.length > 0 &&
													<BulkEditCourseRequest courseID={dataID} schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} courses={courses} students={draftStudents} setStudents={setDraftStudents} sections={sections} setSections={setSections} selectedStudents={selectedStudents} setSelectedStudents={setSelectedStudents} draftMode={true}/>
												}
											</div>
											<div className='btn blue-btn btn-small school-data-student-request-btn edit-course-request-edit-btn' onClick={() => setEditRequestsOpen(true)}>Edit Requests</div>
										</div>
										{sorted_students_requesting_course.length > 0 &&
											<>
											<div className="edit-course-request-header-row edit-data-student-request-row" style={requests_style}>
												<div></div>
												<div><strong>Student Code</strong></div>
												<div><strong>Student Name</strong></div>
												<div><strong>Grade</strong></div>
												<div><strong>Priority</strong></div>
												{scheduleInfo.separate_ICR_codes === '0' && <div className='text-align-center'><strong>Inclusion?</strong></div>}
												<div><strong>Matched?</strong></div>
											</div>
											</>
										}
										<div className='edit-data-list'>
											{sorted_students_requesting_course.length === 0 &&
												<div className='edit-data-no-data-container'>No students have requested this course yet!</div>
											}
									{sorted_students_requesting_course.map((student_entry, index) => {
										const student_info = student_entry;
										const request_info = student_entry.request_info;
										const group_id = student_entry.group_id;
										
										const student_id = student_info.student_id;
										const is_inclusion = request_info.is_inclusion;
										const is_alternate = isAlternateCourseRequest(request_info);
										const request_is_inclusion = is_inclusion === '1';
										const matched = (sections.findIndex(section => {
											if(section.course_periods.length === 0 || section.course_id !== dataID || !section.student_list.includes(student_id)) return false;
											if(scheduleInfo.separate_ICR_codes !== '0') return true;
											return (`${section.is_inclusion || '0'}` === '1') === request_is_inclusion;
										}) !== -1) ? true : false;
										const course_labels = getVisibleRequestLabels({student_labels: student_info.student_labels, course_id: dataID, course_section_label_map: courseSectionLabelMap, labels_by_id: labelsByID});
										
										const composite_key = buildRequestSelectionKey(group_id, student_id);
										const student_selected = selectedStudents.includes(composite_key);												
									return (
											<div className='table-row-inv edit-data-student-request-row' key={index} style={requests_style}>
												<FontAwesomeIcon className={student_selected ? 'blue-text' : 'dark-gray-text'} icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(composite_key)}/>
												<div>{student_info.student_code}</div>
														<div>
															<div>{student_info.last_name}, {student_info.first_name}</div>
															<div>
																	{course_labels.map((label_info, i) => (
																		<div className={`edit-student-schedule-request-label ${getLabelTextColorClass(label_info)}`} key={i}>{label_info.label}</div>
																	))}
															</div>
														</div>
														<div>{student_info.grade}</div>
														<div>
															{is_alternate ? <div className="alternate-color">Alternate</div> : <div>Primary</div>}
														</div>
												{scheduleInfo.separate_ICR_codes === '0' &&
													<div className='text-align-center'>
														<Toggle toggleOn={is_inclusion === '1'} handleChange={() => updateData('change', composite_key, 'student_requests', is_inclusion, 'is_inclusion')} isSmall={true} toggleColor='yellow'/>
													</div>
												}
												<div className={`medium-text ${matched ? 'green-text' : 'gray-text'}`}>{matched ? '' : "Not "}Matched</div>
												<div className="remove-data" onClick={() => updateData('remove', composite_key, 'student_requests')}><FontAwesomeIcon icon={faTimes}/></div>
													</div>
												)	
											})}
										</div>
									</div>
									<div className='edit-data-grid-50'>
										<div className='edit-data-add-new-btn blue-link' onClick={() => toggleAddNewConnection('student_request')}>
											<FontAwesomeIcon icon={faPlusCircle}/>
											<div>Add Student Request</div>
										</div>
										<div className='edit-data-total-requests'>Total Requests: {sorted_students_requesting_course.length}</div>
									</div>
									{addStudentRequestOpen &&
										<div className='search-add-row-container'>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-student-request-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'students')} placeholder='Search for student by name or code'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('student_request')}/>
											</div>
											{studentRequestResults.length !== 0 &&
												<div className='search-add-search-results'>
													<div className='search-add-did-you-mean'>Did you mean:</div>
													{studentRequestResults.map((student_info) => {
														const student_already_requesting = studentHasActiveCourseRequest(student_info, dataID, {isInclusion: false});
														return (
															<div className='search-add-search-result' key={student_info.student_id}>
																<div>
																	<div>{student_info.last_name}, {student_info.first_name}</div>
																	<div className='small-text dark-gray-text'>{student_info.student_code} | Grade {student_info.grade}</div>
																</div>
																<div>
																	{student_already_requesting
																		? <div className='search-add-already-selected'>Selected</div>
																		: <div className='search-add-add-btn' onClick={() => updateData('add', student_info.student_id, 'student_requests')}>Add</div>
																	}
																</div>
															</div>
														)
													})}
												</div>
											}
										</div>
									}
									{editRequestsOpen &&
										<Portal windowName="Manage Student Requests" windowWidth="900" windowHeight="700" cleanUpFunction={() => setEditRequestsOpen(false)}>
											<StudentRequests schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} sections={sections} setSections={setSections} students={draftStudents} setStudents={setDraftStudents} courses={courses} courseID={dataID} draftMode={true} />
										</Portal>
									}
								</div>
								<div>
									<div className='data-modal-heading'>Course Flags</div>
									<div className='edit-data-grid-50 edit-data-other-options-container'>
										<div style={{width:'100%',textAlign:'left'}}>
											<div>Is Lunch?</div>
											<div className='dark-gray-text'>Answer "Yes" if this course is a lunch course</div>
										</div>
										<div className='toggle-with-text-btn-container edit-course-toggle'>
											<div className={`toggle-with-text-btn-optn ${data.is_lunch === '0' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '0', 'is_lunch')}>No</div><div className={`toggle-with-text-btn-optn ${data.is_lunch === '1' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '1', 'is_lunch')}>Yes</div>
										</div>
									</div>
									<div className='edit-data-grid-50 edit-data-other-options-container'>
										<div>
											<div>{scheduleInfo.separate_ICR_codes === '1' ? 'Is Inclusion?' : 'Needs Inclusion Sections?'}</div>
											<div className='dark-gray-text'>{scheduleInfo.separate_ICR_codes === '1' ? 'Answer "Yes" if this course is an inclusion course (i.e. a special ed course that will be combined with a general ed section)' : 'Answer "Yes" if some of the sections for this course will need to be Inclusion sections.'}</div>
										</div>
										<div className='toggle-with-text-btn-container edit-course-toggle'>
											<div className={`toggle-with-text-btn-optn ${data.allow_icr_students === '0' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '0', 'allow_icr_students')}>No</div><div className={`toggle-with-text-btn-optn ${data.allow_icr_students === '1' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '1', 'allow_icr_students')}>Yes</div>
										</div>
									</div>
									<div className='edit-data-grid-50 edit-data-other-options-container'>
										<div>
											<div>Does this course have an extra lab section?</div>
											<div className='dark-gray-text'>Answer "Yes" if this class, on top of having class every day, has an extra class period for lab once a week.</div>
										</div>
										<div className='toggle-with-text-btn-container edit-course-toggle'>
											<div className={`toggle-with-text-btn-optn ${data.is_separate_lab === '0' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '0', 'is_separate_lab')}>No</div><div className={`toggle-with-text-btn-optn ${data.is_separate_lab === '1' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '1', 'is_separate_lab')}>Yes</div>
										</div>
									</div>
									<div className='edit-data-grid-50 edit-data-other-options-container'>
										<div>
											<div>Can a student swap this class for his/her lab?</div>
											<div className='dark-gray-text'>Answer "Yes" if a student is allowed to miss this class to go to an extra lab section once a week.</div>
										</div>
										<div className='toggle-with-text-btn-container edit-course-toggle'>
											<div className={`toggle-with-text-btn-optn ${data.is_swappable_for_lab === '0' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '0', 'is_swappable_for_lab')}>No</div><div className={`toggle-with-text-btn-optn ${data.is_swappable_for_lab === '1' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '1', 'is_swappable_for_lab')}>Yes</div>
										</div>
									</div>
									<div className='edit-data-grid-50 edit-data-other-options-container'>
										<div>
											<div>Should this course be included in the schedule?</div>
											<div className='dark-gray-text'>Answer "No" if this is a class that is offered outside of an official classroom, like an online course or a course at a community college.</div>
										</div>
										<div className='toggle-with-text-btn-container edit-course-toggle'>
											<div className={`toggle-with-text-btn-optn ${data.is_outside_course === '1' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '1', 'is_outside_course')}>No</div><div className={`toggle-with-text-btn-optn ${data.is_outside_course === '0' ? 'toggle-with-text-btn-optn-selected' : ''}`} onClick={(e) => updateData('change', '0', 'is_outside_course')}>Yes</div>
										</div>
									</div>
								</div>
							</form>
						}
					</div>
				</div>
			</div>
		</div>
	);
}
